import React from "react";
import styled from "styled-components";
import tw from "twin.macro";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { ReactComponent as CheckIcon } from "feather-icons/dist/icons/check-circle.svg";
import { ReactComponent as HeartIcon } from "feather-icons/dist/icons/heart.svg";
import { ReactComponent as UsersIcon } from "feather-icons/dist/icons/users.svg";
import { ReactComponent as PlayIcon } from "feather-icons/dist/icons/play-circle.svg";

const SALMON = "#e6806a";
const INK = "#535d58";

const PageWrapper = styled.div`
  ${tw`relative`}
  color: ${INK};
`;

const Container = tw.div`max-w-screen-xl mx-auto px-6 md:px-8`;

// Header simples para evitar dependência das cores do tema global
const Header = styled.header`
  ${tw`w-full flex items-center justify-between py-6`}
`;
const Logo = styled.a`
  ${tw`flex items-center font-black text-2xl`}
  color: ${INK};
  img { ${tw`w-12 h-12 mr-3`} }
`;
const Nav = tw.nav`flex items-center`;
const NavLink = styled.a`
  ${tw`text-sm font-semibold mx-3`}
  color: ${INK};
  &:hover { opacity: .7; }
`;
const PrimaryLink = styled.a`
  ${tw`text-sm font-bold px-6 py-3 rounded-full ml-3`}
  color: #fff;
  background: ${SALMON};
  transition: all .2s ease;
  &:hover { filter: brightness(0.95); }
`;

// Hero
const HeroSection = styled.section`
  ${tw`py-16 md:py-24`}
`;
const HeroInner = tw.div`flex flex-col lg:flex-row lg:items-center`;
const HeroLeft = tw.div`lg:w-6/12 max-w-2xl`;
const HeroRight = tw.div`mt-12 lg:mt-0 lg:w-6/12`;
const Eyebrow = styled.span`
  ${tw`uppercase tracking-wider font-bold`}
  color: ${SALMON};
`;
const Heading = styled.h1`
  ${tw`font-bold text-3xl md:text-4xl lg:text-5xl leading-tight mt-2`}
  color: ${INK};
`;
const Description = styled.p`
  ${tw`mt-4 text-base md:text-lg`}
  color: ${INK};
  opacity: .9;
`;
const Actions = styled.div`
  ${tw`mt-8 flex items-center flex-col sm:flex-row`}
  input {
    ${tw`w-full sm:w-auto rounded-full border-2 px-6 py-4 font-medium`}
    border-color: ${SALMON};
    color: ${INK};
  }
`;
const CtaButton = styled.a`
  ${tw`rounded-full px-8 py-4 font-bold mt-4 sm:mt-0 sm:ml-4`}
  color: #fff;
  background: ${SALMON};
  display: inline-flex;
  align-items: center;
  svg { ${tw`w-5 h-5 ml-2`} }
`;
const HeroImage = tw.img`w-full max-w-3xl rounded-lg`;

// Features
const FeaturesSection = styled.section`
  ${tw`py-16 md:py-24`}
`;
const SectionHeading = styled.h2`
  ${tw`text-2xl md:text-3xl font-extrabold text-center`}
  color: ${INK};
`;
const SectionDescription = styled.p`
  ${tw`mt-3 text-center max-w-2xl mx-auto`}
  color: ${INK};
  opacity: .9;
`;
const FeaturesGrid = tw.div`mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3`;
const FeatureCard = styled.div`
  ${tw`p-8 rounded-2xl shadow`}
  background: #fff;
  border: 1px solid rgba(0,0,0,.05);
`;
const IconCircle = styled.span`
  ${tw`inline-flex items-center justify-center w-12 h-12 rounded-full`}
  background: ${SALMON};
  color: #fff;
`;
const FeatureTitle = styled.h5`
  ${tw`mt-4 font-bold text-lg`}
  color: ${INK};
`;
const FeatureText = styled.p`
  ${tw`mt-2 text-sm`}
  color: ${INK};
  opacity: .9;
`;

// CTA
const CtaSection = styled.section`
  ${tw`py-16 md:py-20`}
  background: rgba(230, 128, 106, 0.08);
`;
const CtaBox = styled.div`
  ${tw`rounded-3xl p-10 md:p-16 text-center`}
  background: #fff;
  border: 1px solid rgba(0,0,0,.05);
`;

// Benefit band (design diferente)
const Band = styled.section`
  ${tw`py-12 md:py-16`}
  background: linear-gradient(180deg, rgba(230,128,106,0.10) 0%, rgba(230,128,106,0.06) 100%);
`;
const BandGrid = tw.div`grid gap-6 md:gap-8 md:grid-cols-3`;
const BandCard = styled.div`
  ${tw`rounded-2xl p-8 md:p-10 shadow`}
  background: #fff;
  border: 1px solid rgba(0,0,0,.06);
`;

// KPIs
const KpiSection = styled.section`${tw`py-12 md:py-16`}`;
const KpiGrid = tw.div`grid grid-cols-2 md:grid-cols-4 gap-6`;
const KpiCard = styled.div`
  ${tw`text-center p-6 rounded-2xl`}
  background: #fff;
  border: 1px solid rgba(0,0,0,.06);
`;
const KpiNumber = styled.div`
  ${tw`font-extrabold text-3xl`}
  color: ${SALMON};
`;
const KpiLabel = styled.div`
  ${tw`mt-2 text-sm`}
  color: ${INK};
`;

// Steps (layout próprio)
const StepsSection = styled.section`${tw`py-12 md:py-16`}`;
const StepsGrid = tw.div`grid gap-6 md:grid-cols-3`;
const StepCard = styled.div`
  ${tw`p-8 rounded-2xl`}
  background: #fff;
  border: 1px solid rgba(0,0,0,.06);
`;
const StepBadge = styled.span`
  ${tw`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold`}
  background: ${SALMON};
  color: #fff;
`;
const StepTitle = styled.h6`
  ${tw`mt-4 font-bold text-lg`}
  color: ${INK};
`;
const StepDesc = styled.p`
  ${tw`mt-2 text-sm`}
  color: ${INK};
  opacity: .9;
`;

// Testimonials simples
const TestimonialsSection = styled.section`${tw`py-12 md:py-16`}`;
const TestimonialsGrid = tw.div`grid gap-6 md:grid-cols-2`;
const TestimonialCard = styled.div`
  ${tw`p-8 rounded-2xl flex`}
  background: #fff;
  border: 1px solid rgba(0,0,0,.06);
`;
const Avatar = tw.img`w-16 h-16 rounded-full mr-4 flex-shrink-0`;
const Quote = styled.p`
  ${tw`text-sm`}
  color: ${INK};
  opacity: .9;
`;
const QuoteName = styled.div`
  ${tw`mt-3 font-semibold`}
  color: ${INK};
`;
const QuoteRole = styled.div`
  ${tw`text-xs`}
  color: ${INK};
  opacity: .8;
`;

// FAQ simples
const FaqSection = styled.section`${tw`py-12 md:py-16`}`;
const FaqGrid = tw.div`grid gap-4 md:gap-6`;
const FaqItem = styled.details`
  ${tw`rounded-2xl p-6`}
  background: #fff;
  border: 1px solid rgba(0,0,0,.06);
  summary { ${tw`font-semibold cursor-pointer`} color: ${INK}; }
  p { ${tw`mt-2 text-sm`} color: ${INK}; opacity: .9; }
`;

// Footer simples
const Footer = styled.footer`
  ${tw`py-10 mt-8 text-center`}
  border-top: 1px solid rgba(0,0,0,.06);
  color: ${INK};
`;
const FooterLink = styled.a`
  ${tw`mx-3 text-sm`}
  color: ${INK};
  opacity: .8;
  &:hover { opacity: 1; }
`;

// Componentes para Anamnese IA - Design minimalista
const AnamneseSection = styled.section`${tw`py-16 md:py-20`}`;

const AnamneseGrid = tw.div`mt-12 grid gap-12 lg:grid-cols-2 lg:gap-16 items-center`;

const AnamneseImageSide = styled.div`
  ${tw`relative`}
`;

const AnamneseMainImage = tw.img`w-full rounded-3xl shadow-xl`;

const AnamneseBadge = styled.div`
  ${tw`absolute bg-white rounded-2xl p-4 shadow-lg flex items-center`}
  border: 2px solid ${SALMON};
  bottom: -1.5rem;
  right: -1.5rem;
`;

const BotIcon = tw.span`text-2xl mr-2`;

const BadgeText = styled.span`
  ${tw`font-bold text-sm`}
  color: ${SALMON};
`;

const AnamneseContentSide = styled.div`
  > * + * {
    margin-top: 2rem;
  }
`;

const AnamneseStep = tw.div`flex items-start`;

const StepNumber = styled.div`
  ${tw`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white mr-4`}
  background: ${SALMON};
`;

const StepContent = tw.div``;

const AnamneseStepTitle = styled.h4`
  ${tw`font-bold text-lg mb-2`}
  color: ${INK};
`;

const StepDescription = styled.p`
  ${tw`text-sm leading-relaxed`}
  color: ${INK};
  opacity: .9;
`;

const SecurityNote = styled.div`
  ${tw`mt-8 p-6 rounded-2xl flex items-start`}
  background: rgba(230,128,106,0.08);
  border: 1px solid rgba(230,128,106,0.2);
`;

const SecurityIcon = tw.span`text-xl mr-3 flex-shrink-0`;

const SecurityText = styled.p`
  ${tw`text-sm`}
  color: ${INK};
  opacity: .9;
`;

export default function LPSalmonLandingPage() {
  return (
    <AnimationRevealPage>
      <PageWrapper>
        <Container>
          <Header>
            <Logo href="/">
              Trataz
            </Logo>
            <Nav>
              <NavLink href="/">Início</NavLink>
              <NavLink href="/profissional">Profissionais</NavLink>
              <NavLink href="https://www.trataz.com.br/login">Entrar</NavLink>
              <PrimaryLink href="https://www.trataz.com.br/register">Começar</PrimaryLink>
            </Nav>
          </Header>

          <HeroSection>
            <HeroInner>
              <HeroLeft>
                <Eyebrow>Para profissionais de saúde</Eyebrow>
                <Heading>
                  Simplifique seus atendimentos com uma plataforma completa
                </Heading>
                <Description>
                  A Trataz ajuda fonoaudiólogos, dentistas e outras especialidades a engajar pacientes, padronizar exercícios, enviar lembretes e acompanhar resultados em tempo real.
                </Description>
                <Actions>
                  <input placeholder="Seu melhor e-mail profissional" />
                  <CtaButton href="https://www.trataz.com.br/login">
                    Começar Gratuitamente
                    <PlayIcon />
                  </CtaButton>
                </Actions>
              </HeroLeft>
              <HeroRight>
                <HeroImage src="../doctorHappy.jpg" alt="Profissional de saúde usando a Trataz" />
              </HeroRight>
            </HeroInner>
          </HeroSection>

          <FeaturesSection>
            <SectionHeading>Feito para sua rotina clínica</SectionHeading>
            <SectionDescription>
              Recursos essenciais para ganhar tempo, aumentar a adesão dos pacientes e medir evolução.
            </SectionDescription>
            <FeaturesGrid>
              <FeatureCard>
                <IconCircle>
                  <UsersIcon />
                </IconCircle>
                <FeatureTitle>Gestão de Pacientes</FeatureTitle>
                <FeatureText>Cadastre, acompanhe e organize casos com histórico centralizado.</FeatureText>
              </FeatureCard>
              <FeatureCard>
                <IconCircle>
                  <CheckIcon />
                </IconCircle>
                <FeatureTitle>Protocolos e Exercícios</FeatureTitle>
                <FeatureText>Envie exercícios personalizados com vídeos e instruções claras.</FeatureText>
              </FeatureCard>
              <FeatureCard>
                <IconCircle>
                  <HeartIcon />
                </IconCircle>
                <FeatureTitle>Engajamento Contínuo</FeatureTitle>
                <FeatureText>Lembretes automáticos por WhatsApp e e-mail para manter a adesão.</FeatureText>
              </FeatureCard>
            </FeaturesGrid>
          </FeaturesSection>

          <Band>
            <SectionHeading>Por que escolher a Trataz?</SectionHeading>
            <SectionDescription>
              Um fluxo moderno para o dia a dia clínico, com foco em adesão do paciente e mensuração de resultados.
            </SectionDescription>
            <BandGrid>
              <BandCard>
                <IconCircle><UsersIcon /></IconCircle>
                <FeatureTitle>Centralização</FeatureTitle>
                <FeatureText>Todos os pacientes, exercícios e registros em um só lugar.</FeatureText>
              </BandCard>
              <BandCard>
                <IconCircle><CheckIcon /></IconCircle>
                <FeatureTitle>Padronização</FeatureTitle>
                <FeatureText>Protocolos replicáveis com personalização por caso.</FeatureText>
              </BandCard>
              <BandCard>
                <IconCircle><HeartIcon /></IconCircle>
                <FeatureTitle>Experiência do Paciente</FeatureTitle>
                <FeatureText>App simples, lembretes automáticos e instruções claras.</FeatureText>
              </BandCard>
            </BandGrid>
          </Band>

          <KpiSection>
            <KpiGrid>
              <KpiCard>
                <KpiNumber>500+</KpiNumber>
                <KpiLabel>Profissionais atendidos</KpiLabel>
              </KpiCard>
              <KpiCard>
                <KpiNumber>300%</KpiNumber>
                <KpiLabel>Mais engajamento</KpiLabel>
              </KpiCard>
              <KpiCard>
                <KpiNumber>15 min</KpiNumber>
                <KpiLabel>Economia diária</KpiLabel>
              </KpiCard>
              <KpiCard>
                <KpiNumber>99,9%</KpiNumber>
                <KpiLabel>Disponibilidade</KpiLabel>
              </KpiCard>
            </KpiGrid>
          </KpiSection>

          <StepsSection>
            <SectionHeading>Como funciona</SectionHeading>
            <SectionDescription>Três passos simples para começar.</SectionDescription>
            <StepsGrid>
              <StepCard>
                <StepBadge>1</StepBadge>
                <StepTitle>Crie sua conta</StepTitle>
                <StepDesc>Cadastre-se e personalize seu perfil profissional.</StepDesc>
              </StepCard>
              <StepCard>
                <StepBadge>2</StepBadge>
                <StepTitle>Configure os exercícios</StepTitle>
                <StepDesc>Escolha protocolos ou crie vídeos e instruções personalizadas.</StepDesc>
              </StepCard>
              <StepCard>
                <StepBadge>3</StepBadge>
                <StepTitle>Compartilhe e acompanhe</StepTitle>
                <StepDesc>Envie ao paciente e monitore adesão e evolução.</StepDesc>
              </StepCard>
            </StepsGrid>
          </StepsSection>

          <TestimonialsSection>
            <SectionHeading>O que dizem os profissionais</SectionHeading>
            <SectionDescription>Resultados práticos no dia a dia.</SectionDescription>
            <TestimonialsGrid>
              <TestimonialCard>
                <Avatar src="../edilaine_profissional.jpg" alt="Dra. Edilaine" />
                <div>
                  <Quote>“A adesão dos pacientes aumentou muito com os lembretes e vídeos. Ficou fácil padronizar o tratamento.”</Quote>
                  <QuoteName>Dra. Edilaine Tannouri</QuoteName>
                  <QuoteRole>Fonoaudióloga</QuoteRole>
                </div>
              </TestimonialCard>
              <TestimonialCard>
                <Avatar src="../carlos_antonio.jpg" alt="Dr. Carlos" />
                <div>
                  <Quote>“Consigo acompanhar a evolução de forma objetiva e ganhar tempo em cada consulta.”</Quote>
                  <QuoteName>Dr. Carlos Antônio</QuoteName>
                  <QuoteRole>Dentista</QuoteRole>
                </div>
              </TestimonialCard>
            </TestimonialsGrid>
          </TestimonialsSection>

          <FaqSection>
            <SectionHeading>Dúvidas frequentes</SectionHeading>
            <SectionDescription>Transparência antes de começar.</SectionDescription>
            <FaqGrid>
              <FaqItem>
                <summary>O paciente precisa pagar?</summary>
                <p>O acesso do paciente ao app é gratuito. O profissinal pode assinar nosso plano Premium para ter acesso a mais recursos e suporte.</p>
              </FaqItem>
              <FaqItem>
                <summary>Preciso instalar algo?</summary>
                <p>Não. A plataforma é online e funciona em qualquer dispositivo moderno.</p>
              </FaqItem>
              <FaqItem>
                <summary>Posso cancelar quando quiser?</summary>
                <p>Sim. Você pode cancelar a qualquer momento sem fidelidade.</p>
              </FaqItem>
            </FaqGrid>
          </FaqSection>

          <AnamneseSection>
            <SectionHeading>Anamnese Inteligente com IA</SectionHeading>
            <SectionDescription>
              Tecnologia de ponta que revoluciona suas consultas. Nossa IA escuta, 
              compreende e documenta automaticamente cada sessão.
            </SectionDescription>
            
            <AnamneseGrid>
              <AnamneseImageSide>
                <AnamneseMainImage 
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
                  alt="Inteligência Artificial analisando consulta"
                  onError={(e) => {
                    e.target.src = "../doctorHappy.jpg";
                  }}
                />
                <AnamneseBadge>
                  <BotIcon>🤖</BotIcon>
                  <BadgeText>IA Avançada</BadgeText>
                </AnamneseBadge>
              </AnamneseImageSide>
              
              <AnamneseContentSide>
                <AnamneseStep>
                  <StepNumber>1</StepNumber>
                  <StepContent>
                    <AnamneseStepTitle>Escuta Ativa</AnamneseStepTitle>
                    <StepDescription>
                      A IA monitora discretamente sua consulta, capturando informações 
                      importantes sem interromper o fluxo natural da conversa.
                    </StepDescription>
                  </StepContent>
                </AnamneseStep>
                
                <AnamneseStep>
                  <StepNumber>2</StepNumber>
                  <StepContent>
                    <AnamneseStepTitle>Análise Inteligente</AnamneseStepTitle>
                    <StepDescription>
                      Processa automaticamente sintomas, histórico e observações clínicas, 
                      organizando tudo de forma estruturada e profissional.
                    </StepDescription>
                  </StepContent>
                </AnamneseStep>
                
                <AnamneseStep>
                  <StepNumber>3</StepNumber>
                  <StepContent>
                    <AnamneseStepTitle>Relatório Personalizado</AnamneseStepTitle>
                    <StepDescription>
                      Gera instantaneamente um relatório completo e específico para sua 
                      área, pronto para ser usado em prontuários e acompanhamentos.
                    </StepDescription>
                  </StepContent>
                </AnamneseStep>
                
                <SecurityNote>
                  <SecurityIcon>🔒</SecurityIcon>
                  <SecurityText>
                    <strong>Totalmente seguro:</strong> Dados criptografados, servidores no Brasil, 
                    100% conforme LGPD. Sua privacidade e a dos pacientes é nossa prioridade.
                  </SecurityText>
                </SecurityNote>
              </AnamneseContentSide>
            </AnamneseGrid>
          </AnamneseSection>

          <CtaSection>
            <CtaBox>
              <SectionHeading>Pronto para elevar seus resultados clínicos?</SectionHeading>
              <SectionDescription>
                Teste por 7 dias, sem cartão de crédito. Cancelamento a qualquer momento.
              </SectionDescription>
              <CtaButton href="https://www.trataz.com.br/register">Criar conta grátis</CtaButton>
            </CtaBox>
          </CtaSection>

          <CtaSection>
            <CtaBox>
              <SectionHeading>Fale com a nossa equipe</SectionHeading>
              <SectionDescription>Quer ver uma demo guiada? Podemos ajudar a adaptar à sua rotina.</SectionDescription>
              <CtaButton href="https://wa.me/5547996912977">Agendar demonstração</CtaButton>
            </CtaBox>
          </CtaSection>

          <Footer>
            <div>
              © {new Date().getFullYear()} Trataz — Plataforma para profissionais da saúde
            </div>
            <div tw="mt-3">
              <FooterLink href="/termos-de-uso">Termos de Uso</FooterLink>
              <FooterLink href="/politica-de-privacidade">Política de Privacidade</FooterLink>
              <FooterLink href="mailto:trataz.oficial@gmail.com">Contato</FooterLink>
            </div>
          </Footer>
        </Container>
      </PageWrapper>
    </AnimationRevealPage>
  );
}

